/**
 * Custom Lexical node for now-read embed elements
 */

import { yupResolver } from '@hookform/resolvers/yup';
import { useQuery } from '@tanstack/react-query';
import { $createParagraphNode, $getSelection, $isRangeSelection, type ElementFormatType, type LexicalNode, type NodeKey } from 'lexical';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import * as yup from "yup";

import { SystemDialogContent } from '@cms/components/data/Dialog';
import { ISystemStory, shortenText, stripTags, TStoryContentElementTypeCustomEmbed, TStoryContentElementTypeCustomEmbedNowRead } from '@dl/declarations';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import api from '../../../utils/api';
import { generateStoryLink } from '../../../utils/helpers';
import { ControlledSystemTextInput } from '../../data/TextInput';
import { Loader } from '../../ui/Loader';
import { CustomEmbedNode, SerializeEmbedNode } from './CustomEmbedNode';
import { CustomEmbedPluginActions, CustomEmbedPluginContainer } from './CustomEmbedPluginContainer';

type TBaseType = TStoryContentElementTypeCustomEmbed;
type TExtendedType = TStoryContentElementTypeCustomEmbedNowRead;


const getSlug = (url: string): string => {
    return url.trim().split('/').filter(Boolean).pop() || ''
}

interface EmbedNowReadProps {
    config: TBaseType;
}

export const EmbedNowRead: React.FC<EmbedNowReadProps> = ({ config }) => {
    const { url = "", text } = (config as TExtendedType).embed.config;

    const slug = getSlug(url);

    const { data: story, isLoading, isError } = useQuery({
        queryKey: ['story', 'embed-now-read', slug],
        queryFn: async () => {
            const response = await api.get<ISystemStory>(`/stories/${slug}/public-preview`);
            return response.data;
        },
        enabled: !!slug,
    })

    const usedText = stripTags(text || (story?.content_elements || []).find((ce) => ce.type === 'text')?.content || '')

    if (isError) {
        return (
            <div className="my-8 border-b border-t border-b-gray-25 border-t-gray-25 py-6">
                <div className="text-center text-gray-500">Error loading story for this embed.</div>
            </div>
        )
    }

    if (isLoading) {
        return <Loader loaderSize='sm' />
    }

    if (!slug || !story) {
        return (
            <div className="my-8 border-b border-t border-b-gray-25 border-t-gray-25 py-6">
                <div className="text-center text-gray-500">No story found for this embed.</div>
            </div>
        )
    }

    return (
        <div className="my-8 border-b border-t border-b-gray-25 border-t-gray-25 py-6">
            <div className="grid grid-cols-3 gap-5 xl:grid-cols-2 xl:gap-6">
                <div className="col-span-1">
                    <a href={generateStoryLink(story)}>
                        {story?.mainAssetObject?.src_url ? (
                            <img
                                src={story?.mainAssetObject?.src_url}
                                alt={story?.headline}
                                width={274}
                                height={274}
                                className='md:w-[400px] md:h-[191px]'
                            />
                        ) : (
                            <div className="bg-blue-35" style={{ aspectRatio: '173/100' }}></div>
                        )}
                    </a>
                </div>

                <div className="col-span-2 xl:col-span-1">
                    <a
                        href={generateStoryLink(story)}
                        className={`block text-[14px] font-bold leading-[150%] text-blue-900 hover:underline dark:text-white xl:text-[20px]`}
                    >
                        {story.headline}
                    </a>

                    {usedText && (
                        <div className="mt-2.5 font-sans-2 text-[12px] leading-[150%] text-blue-900 dark:text-white xl:text-[14px]">
                            <span className="lg:hidden">{shortenText(usedText, 60)}</span>
                            <span className="hidden lg:block">{shortenText(usedText, 130)}</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export const EditEmbedNowRead: React.FC<
    EmbedNowReadProps & {
        handleSubmit?: (payload: TExtendedType) => void;
    }
> = ({ config, handleSubmit }) => {
    const usedConfig = config as TExtendedType;

    const [editor] = useLexicalComposerContext();

    const methods = useForm({
        resolver: yupResolver(yup.object().shape({
            url: yup.string().url().required().label("URL"),
            text: yup.string().optional().label("Text"),
        })),
        defaultValues: {
            url: usedConfig.embed.config.url || '',
            text: usedConfig.embed.config.text || ''
        }
    })

    const { handleSubmit: handleFormSubmit } = methods;

    const handleSubmitEdits = handleFormSubmit((data) => {
        editor.update(() => {
            const payload: TExtendedType = {
                ...usedConfig,
                embed: {
                    ...usedConfig.embed,
                    config: {
                        ...usedConfig.embed.config,
                        ...data
                    }
                }
            }

            if (handleSubmit) {
                handleSubmit(payload)
            }
            else {
                const selection = $getSelection();

                if ($isRangeSelection(selection) && selection.isCollapsed()) {
                    const node = $createNowReadNode(payload as TStoryContentElementTypeCustomEmbed);
                    selection.insertNodes([node, $createParagraphNode()]);
                }
            }
        })
    })

    return (
        <CustomEmbedPluginContainer
            embedTitle='Embed/Now Read'
            embedDescription={(
                <>
                    <b>Add quick views and access to other articles</b> related to the current text between paragraphs.
                </>
            )}
            instructionsScreenshotUrl='/images/embeds/now-read.png'
            embedActionComponent={(
                <FormProvider {...methods}>
                    <form className="flex flex-col gap-4">
                        <ControlledSystemTextInput
                            name='url'
                            inputSize='full'
                            placeholder="Article URL"
                        />

                        <ControlledSystemTextInput
                            name='text'
                            inputSize='full'
                            placeholder="Add Block Description"
                        />

                        <CustomEmbedPluginActions
                            handleSubmit={handleSubmitEdits}
                        />
                    </form>
                </FormProvider>
            )}
        />
    );
}

export const EditEmbedNowReadDialog: React.FC<
    EmbedNowReadProps
> = ({ config }) => {
    return (
        <SystemDialogContent
            dialogProps={{
                title: 'Embed/Now Read',
            }}
            content={{
                component: (
                    <EditEmbedNowRead config={config} />
                ),
            }}
            actionsProps={{
                hide: true
            }}
        />
    )
}
export class CustomNowReadNode extends CustomEmbedNode {
    __config: TBaseType;

    constructor(
        config: TBaseType,
        format?: ElementFormatType,
        key?: NodeKey,
    ) {
        super(
            "Embed Now Read",
            <EmbedNowRead config={config} />,
            <EditEmbedNowRead config={config} handleSubmit={(payload) => { this.handleSubmit(payload) }} />,
            config,
            format,
            key
        );
        this.__config = config;
    }

    handleSubmit(config: TExtendedType) {
        this.setConfig(config as TBaseType);
    }

    static getType(): string {
        return "now-read"
    }

    static clone(node: CustomNowReadNode): CustomNowReadNode {
        return new CustomNowReadNode(node.__config, node.__format, node.__key);
    }

    static importJSON(serializedNode: SerializeEmbedNode): CustomNowReadNode {
        return $createNowReadNode(
            serializedNode.config,
        ).updateFromJSON(
            serializedNode,
        );
    }
}

export function $isNowReadNode(
    node: LexicalNode | null | undefined,
): node is CustomNowReadNode {
    return node instanceof CustomNowReadNode;
}

export function $createNowReadNode(
    config: TBaseType,
): CustomNowReadNode {
    return new CustomNowReadNode(config);
}
